import React, { useState } from 'react';
import { Layout, Dropdown, Space, Drawer, Avatar, Button } from 'antd';
import { LogoutOutlined, SettingOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { MainContent } from './MainContent';
import { type BreadcrumbItem } from '@/shared/lib/breadcrumb-context';
import { layoutStyles } from '@/shared/styles/layoutStyles';
import { useAuth } from '@/shared/lib/auth-context';
import { SettingsPage } from '@/features/settings/pages/SettingsPage';
import { CreateGuideDrawer } from '@/features/dashboard/components/CreateGuideDrawer';
import { useGuideCreation } from '@/features/guides/hooks/useGuideCreation';
import { useNotification } from '@/shared/hooks/useNotification';
import { useSearchContext } from '@/features/dashboard/context/SearchContext';
import type { MenuProps } from 'antd';

export const DemoLayout: React.FC = () => {
  const [customBreadcrumbs] = useState<BreadcrumbItem[] | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);
  const { t } = useTranslation();
  const { logout, user } = useAuth();
  const { contextHolder, notification } = useNotification();
  const { problemDescription, vehicleData } = useSearchContext();
  
  const handleSuccess = () => {
    notification.success(t('guides.createSuccess'));
  };
  
  const { creationState, openCreationDialog, closeCreationDialog, ...guideCreationProps } = useGuideCreation({
    onSuccess: handleSuccess
  });

  // Get user initials
  const getUserInitials = () => {
    if (!user?.name) return 'U';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return user.name.substring(0, 2).toUpperCase();
  };

  const handleLogout = () => {
    logout();
    setDropdownOpen(false);
  };

  const handleSettings = () => {
    setSettingsDrawerOpen(true);
    setDropdownOpen(false);
  };

  const items: MenuProps['items'] = [
    {
      key: 'settings',
      label: (
        <Space>
          <SettingOutlined />
          {t('navigation.settings')}
        </Space>
      ),
      onClick: handleSettings,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: (
        <Space>
          <LogoutOutlined />
          {t('navigation.logout')}
        </Space>
      ),
      onClick: handleLogout,
    },
  ];

  return (
    <Layout style={layoutStyles.mainLayout}>
      <MainContent 
        collapsed={true}
        width={0}
        collapsedWidth={0}
        customBreadcrumbs={customBreadcrumbs}
        setCustomBreadcrumbs={() => {}}
        hideHeader={true}
      />
      
      {/* Create Guide floating button */}
      <Button
        className="create-guide-btn"
        type="primary"
        icon={<PlusOutlined />}
        size="large"
        onClick={() => {
          openCreationDialog(problemDescription || '', vehicleData || {});
        }}
        style={{
          position: 'fixed',
          top: 'clamp(12px, 3vw, 17px)',
          right: 'calc(clamp(8px, 2vw, 12px) + clamp(40px, 10vw, 48px) + 12px)',
          height: 48,
          borderRadius: 24,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 'clamp(14px, 2.5vw, 16px)',
          padding: '0 clamp(16px, 3vw, 24px)',
        }}
      >
        {t('demo.searchResults.createNewGuide')}
      </Button>

      {/* User avatar with dropdown */}
      <Dropdown
        menu={{ items }}
        placement="bottomRight"
        trigger={['click']}
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
      >
        <Avatar
          className="user-avatar"
          size={48}
          style={{
            position: 'fixed',
            top: 'clamp(12px, 3vw, 17px)',
            right: 'clamp(8px, 2vw, 12px)',
            backgroundColor: '#1890ff',
            color: 'white',
            fontWeight: 600,
            fontSize: 'clamp(14px, 3vw, 18px)',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 'clamp(40px, 10vw, 48px)',
            height: 'clamp(40px, 10vw, 48px)'
          }}
        >
          {getUserInitials()}
        </Avatar>
      </Dropdown>

      <Drawer
        title={t('navigation.settings')}
        placement="right"
        width={600}
        onClose={() => setSettingsDrawerOpen(false)}
        open={settingsDrawerOpen}
        styles={{
          body: {
            padding: 0,
          },
        }}
      >
        <div style={{ padding: '24px' }}>
          <SettingsPage />
        </div>
      </Drawer>

      <CreateGuideDrawer
        open={creationState.isOpen}
        onClose={closeCreationDialog}
        {...guideCreationProps}
      />

      {contextHolder}
    </Layout>
  );
};